import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

type UseSkipLinkParams = {
  label?: string;
  targetId: string;
};

type UseSkipLinkReturn = {
  accessibleLabel: string;
  href: string;
  onClick: (event: MouseEvent<HTMLAnchorElement>) => void;
};

function useSkipLink({ label, targetId }: UseSkipLinkParams): UseSkipLinkReturn {
  const { t } = useTranslation();

  // `||` y no `??` a propósito: una cadena vacía debe caer también a la traducción, porque un
  // `<a>` sin texto es un enlace sin nombre accesible (violación de "link name" en axe).
  const accessibleLabel = label || t('common:SkipToContent', { defaultValue: 'Skip to content' });
  const href = `#${targetId}`;

  /**
   * Siempre prevenimos el salto nativo: con `BrowserRouter` el hash acabaría en la URL de la ruta
   * actual (y en el historial), y Safari además no mueve el foco al destino del ancla — sólo hace
   * scroll. Movemos el foco a mano al `<main tabIndex={-1}>`.
   *
   * `preventScroll: true` es imprescindible, no una optimización: Chrome alinea el elemento
   * enfocado con "center if needed", y como el `<main>` es más alto que el viewport, centrarlo
   * deja al usuario a media página del contenido en vez de al principio.
   *
   * Tampoco lo sustituimos por `scrollIntoView({ block: 'start' })`: eso pondría el borde superior
   * del `<main>` al ras del viewport y el header sticky (`.layout-header__header`, top: 0) taparía
   * la primera franja del contenido. Sin scroll, la vista se queda donde estaba — con el header
   * visible y el contenido justo debajo, que es el sitio correcto al que llega el enlace.
   */
  function onClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();

    const target = document.getElementById(targetId);
    if (!target) return;

    target.focus({ preventScroll: true });
  }

  return { accessibleLabel, href, onClick };
}

export default useSkipLink;
